import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import ConfirmDialog from "components/common/ConfirmDialog";
import { type ScheduleItem } from "features/integrationsBeta/integrationsBeta";
import {
  useDeleteScheduleMutation,
  useGetSchedulesQuery,
} from "features/integrationsBeta/schedules";
import { usePlatformRole } from "features/integrationsBeta/usePlatformRole";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useState } from "react";
import { summarizeAwsCron } from "utils/awsCron";
import {
  usePendingChanges,
  usePendingValue,
} from "./pending/PendingChangesContext";
import ScheduleBuilderDialog from "./ScheduleBuilderDialog";
import SectionCard from "./SectionCard";
import TimezoneSelect from "./TimezoneSelect";

const scheduleLabel = (s: ScheduleItem) =>
  `Schedule "${s.display_name || s.name}"`;

interface Props {
  integrationId: number;
  timezone: string;
  // Rendered on a webhook-mode integration that still has schedule rows:
  // warn + delete-only (scheduled runs would fail on the empty payload).
  orphanMode?: boolean;
}

export default function SchedulesSection({
  integrationId,
  timezone,
  orphanMode = false,
}: Props) {
  const dispatch = useAppDispatch();
  const { isSuperuser } = usePlatformRole();
  const { data: schedules = [] } = useGetSchedulesQuery(integrationId);
  const { stage, getPending } = usePendingChanges();
  const [deleteSchedule, { isLoading: deleting }] = useDeleteScheduleMutation();
  const [dialog, setDialog] = useState<{ schedule?: ScheduleItem } | null>(
    null,
  );
  const [confirmDelete, setConfirmDelete] = useState<ScheduleItem | null>(null);

  const canWrite = isSuperuser && !orphanMode;

  const notifyError = (message: string) =>
    dispatch(pushNotification({ severity: "error", message }));

  const [effectiveTimezone] = usePendingValue(
    "integration",
    integrationId,
    "timezone",
    timezone,
  );

  const handleToggle = (s: ScheduleItem, is_active: boolean) =>
    stage({
      entityType: "schedule",
      entityId: s.id,
      section: "schedules",
      entityLabel: scheduleLabel(s),
      field: "is_active",
      fieldLabel: "Active",
      valueKind: "active",
      oldValue: s.is_active,
      newValue: is_active,
    });

  const handleTimezone = (tz: string) =>
    stage({
      entityType: "integration",
      entityId: integrationId,
      section: "schedules",
      entityLabel: "Integration",
      field: "timezone",
      fieldLabel: "Timezone",
      valueKind: "text",
      oldValue: timezone,
      newValue: tz,
    });

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteSchedule({
        id: confirmDelete.id,
        integration: integrationId,
      }).unwrap();
      dispatch(
        pushNotification({ severity: "success", message: "Schedule deleted." }),
      );
    } catch {
      notifyError("Could not delete the schedule.");
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <SectionCard
      title="Schedules"
      action={
        canWrite ? (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setDialog({})}
          >
            Add schedule
          </Button>
        ) : undefined
      }
    >
      {orphanMode && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          This integration is webhook-driven (a step consumes the webhook
          payload). Scheduled runs would fail on the empty payload - delete
          these schedules.
        </Alert>
      )}

      {!orphanMode && (
        <Box sx={{ mb: 1.5 }}>
          {isSuperuser ? (
            <TimezoneSelect
              value={effectiveTimezone}
              onChange={handleTimezone}
              label="Integration timezone"
            />
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              All schedules run in the integration timezone ({timezone}).
            </Typography>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.5 }}
          >
            All schedules run in this timezone.
          </Typography>
        </Box>
      )}

      <Stack spacing={1}>
        {schedules.length ? (
          schedules.map((s) => {
            const stagedName =
              (getPending("schedule", s.id, "display_name")?.newValue as
                | string
                | undefined) ??
              (s.display_name || s.name);
            const stagedCron =
              (getPending("schedule", s.id, "cron_expression")?.newValue as
                | string
                | undefined) ?? s.cron_expression;
            const stagedActive =
              (getPending("schedule", s.id, "is_active")?.newValue as
                | boolean
                | undefined) ?? s.is_active;
            const hasPending =
              !!getPending("schedule", s.id, "display_name") ||
              !!getPending("schedule", s.id, "cron_expression") ||
              !!getPending("schedule", s.id, "is_active");
            return (
              <Box
                key={s.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {stagedName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {summarizeAwsCron(stagedCron)}
                  </Typography>
                </Box>
                {hasPending && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="warning"
                    label="Unsaved changes"
                  />
                )}
                <Tooltip title={stagedActive ? "Active" : "Inactive"}>
                  <Switch
                    size="small"
                    checked={stagedActive}
                    disabled={!canWrite}
                    onChange={(_, c) => handleToggle(s, c)}
                  />
                </Tooltip>
                {canWrite && (
                  <Tooltip title="Edit schedule">
                    <IconButton
                      size="small"
                      onClick={() => setDialog({ schedule: s })}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {isSuperuser && (
                  <Tooltip title="Delete schedule">
                    <IconButton
                      size="small"
                      onClick={() => setConfirmDelete(s)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary">
            No schedules. This integration runs on manual / external triggers
            only.
          </Typography>
        )}
      </Stack>

      {dialog && (
        <ScheduleBuilderDialog
          open
          onClose={() => setDialog(null)}
          integrationId={integrationId}
          schedule={dialog.schedule}
        />
      )}

      <ConfirmDialog
        open={confirmDelete != null}
        title="Delete schedule"
        message={`Delete schedule "${
          confirmDelete?.display_name || confirmDelete?.name || ""
        }"? The EventBridge schedule is removed as well.`}
        confirmLabel="Delete"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </SectionCard>
  );
}
